import React, { useEffect } from "react"
import { useInView } from "react-intersection-observer"
import { useAuth, useFeatureFlag } from "hooks"
import {
  Box,
  Text,
  Flex,
  Link as ChakraLink,
  useDisclosure,
} from "@chakra-ui/react"
import type { VariantType } from "@shared/graphql.types"
import { BonusDayBanner } from "components/ListBanners/BonusDayBanner"
import { ShadeSelector } from "packages/core-shopping/components/ShadeSelector"
import { ReadMoreV2 } from "components/ReadMoreV2"
import { dispatch } from "@shared/utils.events"
import { useVariantIds } from "hooks/useVariantIds"
import { useExperimentViewed } from "tracking/hooks/useExperimentViewed"
import { useSegment } from "tracking"
import { ExperimentFlags } from "@shared/utils.experimentation"
import { InfoIcon } from "components/SVG/InfoIcon"
import { OriginCountry } from "./OriginCountry"
import { FlexContainer, SizeText } from "./CoreProductInfo.style"
import { RatingPDP } from "./RatingPDP"
import { ShareProductPDP } from "./SharePDP"
import { ProductComparisonSkus } from "../../PDPPage.constants"
import { ComparisonDrawer } from "./ComparisonDrawer"
import {
  comparisonContainer,
  comparisonText,
  comparisonTextContent,
  infoIcon,
} from "./CoreProductInfo.css"
import { TraditionalRetailBanner } from "packages/acquisition/components/GuestPrice/TraditionalRetailBanner"

interface Props {
  variant: VariantType
  isModalOpen: boolean
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  reviewAnchor?: boolean
}

export const CoreProductInfo: React.FC<Props> = ({
  variant,
  isModalOpen,
  setIsModalOpen,
  reviewAnchor,
}) => {
  const {
    id,
    type,
    brandName,
    title,
    size,
    elevatorPitch,
    longDescription,
    origin,
    suitableFor,
    subTitle,
    availability,
    pricing,
    review,
    sku,
  } = variant
  const { isAuthenticated } = useAuth()
  const { track } = useSegment()
  const isSize = type === "size"
  const { giftMembershipProductId } = useVariantIds()
  const isGiftMembership = giftMembershipProductId === variant.id
  const productComparison = ProductComparisonSkus[sku]
  const { isOpen, onOpen, onClose } = useDisclosure()

  const reviewLink = reviewAnchor ? `/products/${variant.slug}` : ""

  const [isLoading, productComparisonVariation] = useFeatureFlag(
    ExperimentFlags.PRODUCT_COMPARISON_EXPERIMENT_V3,
    "0"
  )

  const trackExperimentViewed = useExperimentViewed()

  useEffect(() => {
    if (!isLoading) {
      trackExperimentViewed(
        ExperimentFlags.PRODUCT_COMPARISON_EXPERIMENT_V3,
        Number(productComparisonVariation)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const { inView, ref } = useInView({ threshold: 1, triggerOnce: true })

  useEffect(() => {
    if (inView) {
      track({
        event: "Page Element Viewed",
        properties: {
          module_content: "dupes_v3_compare",
          location_type: "page",
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  const showBonusDayBanner = availability?.label
    ?.toLowerCase()
    ?.includes("bonus day")

  let shadeTitle = ""
  if (subTitle) {
    // remove text between brackets e.g. XXX (YYY) => XXX
    shadeTitle = ` (${subTitle.replace(/ *\([^)]*\) */g, "")})`
  }
  const { stars: reviewStars, count: reviewCount } = review || {}
  const sizeArray = size?.split("|")

  const trackReviewsClicked = () => {
    dispatch("page element clicked", {
      target: "PDP Product Ratings",
    })
  }

  const showShadeSelector =
    variant?.relatedVariants &&
    !!(variant?.relatedVariants?.length > 1) &&
    !isSize

  return (
    <>
      {review && (
        <FlexContainer mt={{ base: 1, lg: 0 }}>
          <ChakraLink
            title="View Reviews"
            href={`${reviewLink}#reviews`}
            target={reviewAnchor ? "_blank" : "_self"}
            onClick={trackReviewsClicked}
          >
            <RatingPDP
              reviewStars={reviewStars ?? 0}
              outOfStock={false}
              reviewCount={reviewCount ?? 0}
              showText
            />
          </ChakraLink>

          <Box display={{ base: "none", lg: "unset" }}>
            <ShareProductPDP variant={variant} />
          </Box>
        </FlexContainer>
      )}
      <Box>
        <Box display="flex" flexDirection="column">
          <Text fontSize="1.75rem" lineHeight="2.5rem" m={0} as="h1">
            <Text variant="acuminMedium">{brandName}</Text>
            <Text variant="acuminLight" mt={0}>{`${title}${shadeTitle}`}</Text>
          </Text>
        </Box>
        <TraditionalRetailBanner pricing={variant.pricing} />
        <Flex alignItems="center" mt={1}>
          {sizeArray?.map((amount) => {
            return (
              <SizeText
                key={amount}
                variant="acuminLight"
                sx={{ borderRight: "none" }}
                alignItems="center"
                mt={0}
                maxWidth="200px"
              >
                {amount?.trim()}
              </SizeText>
            )
          })}

          {origin?.code && origin?.name && (
            <OriginCountry
              flexWrap="wrap"
              mt={0}
              order={{ base: 2, md: "unset" }}
              countryCode={origin?.code}
              madeInLabel={origin?.name}
            />
          )}
        </Flex>
        {productComparison &&
          ["1", "2", "3"].includes(productComparisonVariation) && (
            <div
              className={comparisonContainer}
              ref={ref}
              role="button"
              tabIndex={0}
              onClick={() => {
                track({
                  event: "Page Element Clicked",
                  properties: {
                    module_content: "dupes_v3_compare",
                    module_content_details: "compare_to",
                    module_content_details2: sku,
                    module_content_details3: productComparison.brand,
                    location_type: "page",
                  },
                })
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  track({
                    event: "Page Element Clicked",
                    properties: {
                      module_content: "dupes_v3_compare",
                      module_content_details: "compare_to",
                      module_content_details2: sku,
                      module_content_details3: productComparison.brand,
                      location_type: "page",
                    },
                  })
                }
              }}
            >
              <div className={comparisonTextContent}>
                <div className={comparisonText}>
                  <b>Compare To:</b> {productComparison.copy}
                </div>
                {productComparisonVariation !== "3" && (
                  <InfoIcon className={infoIcon} onClick={onOpen} />
                )}
              </div>
              <ComparisonDrawer
                productComparison={productComparison}
                variantData={[id, sku, pricing]}
                isOpen={isOpen}
                productComparisonVariation={productComparisonVariation}
                onClose={onClose}
              />
            </div>
          )}
        {showBonusDayBanner && isAuthenticated && (
          <Box mb={-1}>
            <BonusDayBanner />
          </Box>
        )}
        <Text variant="acuminMedium">{elevatorPitch}</Text>
        {longDescription && (
          <ReadMoreV2
            text={longDescription}
            suitableFor={suitableFor ?? ""}
            isGiftMembership={isGiftMembership}
          />
        )}

        {showShadeSelector && (
          <ShadeSelector
            variant={variant}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
          />
        )}
      </Box>
    </>
  )
}
